import React, { useEffect, useState, useCallback } from 'react';
import { getSwaps, acceptSwap, createSwapRequest, deleteSwapRequest, withdrawSwap, SwapRequestData } from '../../services/swapService';
import { format, parseISO } from 'date-fns';
import apiClient from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const statusStyles: Record<string, string> = {
    PENDING: 'bg-amber-50 text-amber-700 border border-amber-200',
    APPROVED: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    REJECTED: 'bg-red-50 text-red-700 border border-red-200',
};

const shiftTypeStyles: Record<string, { bg: string; label: string }> = {
    CLEARER:   { bg: 'bg-sky-100 text-sky-700',     label: 'Clearing' },
    LHR:       { bg: 'bg-indigo-100 text-indigo-700', label: 'LHR' },
    OFFICE:    { bg: 'bg-teal-100 text-teal-700',    label: 'Office' },
    WAREHOUSE: { bg: 'bg-orange-100 text-orange-700', label: 'Warehouse' },
    CONTROL:   { bg: 'bg-violet-100 text-violet-700', label: 'Control' },
};

// When a shift has no location set, infer it from the type
const impliedLocation: Record<string, string> = {
    LHR: 'Heathrow',
    OFFICE: 'Office',
    CONTROL: 'Control Room',
    WAREHOUSE: 'Warehouse',
};

const getShiftLocation = (type: string, location?: string | null): string | null => {
    if (location) return location;
    return impliedLocation[type] || null;
};

const shiftTypeLabel = (type?: string) => type ? (shiftTypeStyles[type]?.label || type) : 'Shift';
const formatShiftDate = (startTime: string) => format(parseISO(startTime), 'EEE, d MMM yyyy');
const formatShiftRange = (startTime: string, endTime: string) => `${format(parseISO(startTime), 'HH:mm')} - ${format(parseISO(endTime), 'HH:mm')}`;

const ShiftSummary = ({ title, shift, owner }: { title: string; shift: any; owner?: string }) => {
    const loc = getShiftLocation(shift.type || '', shift.location);
    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{title}</p>
                {shift.type && (
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${shiftTypeStyles[shift.type]?.bg || 'bg-slate-100 text-slate-600'}`}>
                        {shiftTypeLabel(shift.type)}
                    </span>
                )}
            </div>
            <p className="font-semibold text-slate-800">{formatShiftDate(shift.startTime)}</p>
            <p className="text-sm text-slate-500">{formatShiftRange(shift.startTime, shift.endTime)}{loc ? ` · ${loc}` : ''}</p>
            {owner && <p className="text-xs text-slate-400 mt-1">{owner}</p>}
        </div>
    );
};

interface UserShift {
    id: string;
    startTime: string;
    endTime: string;
    type?: string;
    location?: string;
    userId?: string;
    userName?: string;
}

const SwapMarketplace: React.FC = () => {
    const { user: authUser } = useAuth();
    const currentUserId = authUser?.id;

    const [activeTab, setActiveTab] = useState<'market' | 'mine' | 'post'>('market');
    const [marketSwaps, setMarketSwaps] = useState<SwapRequestData[]>([]);
    const [mySwaps, setMySwaps] = useState<SwapRequestData[]>([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Post swap state
    const [myShifts, setMyShifts] = useState<UserShift[]>([]);
    const [selectedShiftId, setSelectedShiftId] = useState('');
    const [posting, setPosting] = useState(false);
    const [loadingShifts, setLoadingShifts] = useState(false);
    const [postSwapType, setPostSwapType] = useState<'COVER' | 'SWAP'>('COVER');
    const [availableTargetShifts, setAvailableTargetShifts] = useState<UserShift[]>([]);
    const [selectedTargetShiftId, setSelectedTargetShiftId] = useState('');
    const [loadingTargetShifts, setLoadingTargetShifts] = useState(false);
    const [targetShiftError, setTargetShiftError] = useState('');
    const [targetStaffFilter, setTargetStaffFilter] = useState('');
    const [targetDateFilter, setTargetDateFilter] = useState('');

    // Confirm modal
    const [confirmAction, setConfirmAction] = useState<{ type: 'accept' | 'cancel' | 'withdraw'; id: string; label: string } | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const showToast = (type: 'success' | 'error', message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 4000);
    };

    const fetchSwaps = useCallback(async () => {
        setLoading(true);
        try {
            const [market, mine] = await Promise.all([getSwaps('market'), getSwaps('mine')]);
            setMarketSwaps(Array.isArray(market) ? market : []);
            setMySwaps(Array.isArray(mine) ? mine : []);
        } catch (err: any) {
            console.error('Failed to load swaps', err);
            showToast('error', err.response?.data?.message || 'Failed to load swap requests');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMyShifts = useCallback(async () => {
        setLoadingShifts(true);
        try {
            const res = await apiClient.get('/shifts/my-upcoming');
            setMyShifts(Array.isArray(res.data) ? res.data : res.data?.data || []);
        } catch (err: any) {
            setMyShifts([]);
            showToast('error', err.response?.data?.message || 'Could not load your upcoming shifts');
        } finally {
            setLoadingShifts(false);
        }
    }, []);

    useEffect(() => {
        fetchSwaps();
    }, [fetchSwaps]);

    useEffect(() => {
        if (activeTab === 'post') loadMyShifts();
    }, [activeTab, loadMyShifts]);

    // Load target shifts when user selects a shift + SWAP mode
    useEffect(() => {
        const loadTargetShifts = async () => {
            if (postSwapType !== 'SWAP' || !selectedShiftId) {
                setAvailableTargetShifts([]);
                setSelectedTargetShiftId('');
                setTargetShiftError('');
                return;
            }
            const selectedShift = myShifts.find(s => s.id === selectedShiftId);
            if (!selectedShift) return;

            setLoadingTargetShifts(true);
            setTargetShiftError('');
            try {
                // Get all shifts of the same type that aren't mine
                const res = await apiClient.get(`/shifts/available-for-swap?type=${selectedShift.type || ''}`);
                const shifts = Array.isArray(res.data) ? res.data : res.data?.data || [];
                setAvailableTargetShifts(shifts);
            } catch (err: any) {
                setAvailableTargetShifts([]);
                setTargetShiftError(err.response?.data?.message || 'Could not load matching shifts');
            } finally {
                setLoadingTargetShifts(false);
            }
        };
        loadTargetShifts();
    }, [postSwapType, selectedShiftId, myShifts]);

    const filteredTargetShifts = availableTargetShifts.filter(shift => {
        const staffMatch = !targetStaffFilter.trim()
            || shift.userName?.toLowerCase().includes(targetStaffFilter.trim().toLowerCase());
        const dateMatch = !targetDateFilter
            || format(parseISO(shift.startTime), 'yyyy-MM-dd') === targetDateFilter;
        return staffMatch && dateMatch;
    });

    const clearTargetFilters = () => {
        setTargetStaffFilter('');
        setTargetDateFilter('');
    };

    const handleAccept = async () => {
        if (!confirmAction) return;
        setActionLoading(true);
        try {
            await acceptSwap(confirmAction.id);
            showToast('success', 'Swap accepted! Waiting for admin approval.');
            fetchSwaps();
        } catch (err: any) {
            showToast('error', err.response?.data?.message || 'Failed to accept swap');
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleCancel = async () => {
        if (!confirmAction) return;
        setActionLoading(true);
        try {
            await deleteSwapRequest(confirmAction.id);
            showToast('success', 'Swap request cancelled');
            fetchSwaps();
        } catch {
            showToast('error', 'Failed to cancel swap');
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleWithdraw = async () => {
        if (!confirmAction) return;
        setActionLoading(true);
        try {
            await withdrawSwap(confirmAction.id);
            showToast('success', 'Swap acceptance withdrawn');
            fetchSwaps();
        } catch (err: any) {
            showToast('error', err.response?.data?.message || 'Failed to withdraw');
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handlePostSwap = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedShiftId) {
            showToast('error', 'Please select a shift');
            return;
        }
        if (postSwapType === 'SWAP' && !selectedTargetShiftId) {
            showToast('error', 'Please select the shift you want in exchange');
            return;
        }
        if (postSwapType === 'SWAP' && !availableTargetShifts.some(shift => shift.id === selectedTargetShiftId)) {
            showToast('error', 'The selected target shift is no longer available');
            setSelectedTargetShiftId('');
            return;
        }
        setPosting(true);
        try {
            await createSwapRequest(
                selectedShiftId,
                undefined,
                postSwapType,
                postSwapType === 'SWAP' ? selectedTargetShiftId : undefined
            );
            showToast('success', postSwapType === 'COVER' ? 'Cover request posted!' : 'Swap request posted!');
            setSelectedShiftId('');
            setSelectedTargetShiftId('');
            clearTargetFilters();
            setActiveTab('mine');
            fetchSwaps();
        } catch (err: any) {
            showToast('error', err.response?.data?.message || 'Failed to post');
        } finally {
            setPosting(false);
        }
    };

    const formatShiftTime = (startTime: string, endTime: string) => {
        try {
            const start = parseISO(startTime);
            return `${format(start, 'EEE, MMM d')} · ${formatShiftRange(startTime, endTime)}`;
        } catch {
            return startTime;
        }
    };

    const acceptLabelFor = (swap: SwapRequestData) => {
        if (swap.swapType === 'SWAP') {
            const target = swap.targetShift ? ` for ${formatShiftDate(swap.targetShift.startTime)} (${formatShiftRange(swap.targetShift.startTime, swap.targetShift.endTime)})` : '';
            return `Accept this swap with ${swap.requester.name}${target}. You are agreeing to exchange the specified shifts after admin approval.`;
        }
        return `Cover ${swap.requester.name}'s shift on ${formatShiftDate(swap.shift.startTime)} (${formatShiftRange(swap.shift.startTime, swap.shift.endTime)}). Admin approval is required before the roster changes.`;
    };

    return (
        <div className="max-w-3xl mx-auto">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 text-sm font-medium ${
                    toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                }`} style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    {toast.type === 'success' ? '✓' : '✕'} {toast.message}
                </div>
            )}

            {/* Confirm Modal */}
            {confirmAction && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl" style={{ animation: 'pageFadeIn 0.15s ease-out' }}>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">
                            {confirmAction.type === 'accept' ? 'Accept Swap?' : 'Cancel Swap?'}
                        </h3>
                        <p className="text-sm text-slate-500 mb-5">{confirmAction.label}</p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setConfirmAction(null)}
                                disabled={actionLoading}
                                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAction.type === 'accept' ? handleAccept : confirmAction.type === 'withdraw' ? handleWithdraw : handleCancel}
                                disabled={actionLoading}
                                className={`flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                                    confirmAction.type === 'accept'
                                        ? 'bg-emerald-600 hover:bg-emerald-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                }`}
                            >
                                {actionLoading && <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />}
                                {confirmAction.type === 'accept' ? 'Accept' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-800">Shift Swaps</h1>
                <p className="text-slate-500 text-sm mt-1">Browse available shifts, manage your swap requests, or post a shift</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-6 w-fit">
                {[
                    { key: 'market' as const, label: '🏪 Marketplace', count: marketSwaps.length },
                    { key: 'mine' as const, label: 'My Requests', count: mySwaps.length },
                    { key: 'post' as const, label: '➕ Post Swap' },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                            activeTab === tab.key
                                ? 'bg-white text-slate-800 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        {tab.label}
                        {tab.count !== undefined && tab.count > 0 && (
                            <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">{tab.count}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Marketplace Tab */}
            {activeTab === 'market' && (
                <div style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    {loading ? (
                        <div className="space-y-3">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                                    <div className="flex justify-between">
                                        <div className="space-y-2 flex-1">
                                            <div className="h-4 bg-slate-200 rounded w-2/3" />
                                            <div className="h-3 bg-slate-100 rounded w-1/3" />
                                        </div>
                                        <div className="w-24 h-9 bg-slate-200 rounded-lg" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : marketSwaps.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3"><svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/></svg></div>
                            <h3 className="text-lg font-semibold text-slate-700">No shifts available</h3>
                            <p className="text-slate-500 text-sm mt-1">Only shifts matching your role and skills are shown here</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {marketSwaps.map(swap => (
                                <div key={swap.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-sm transition-all">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-4 flex-1 min-w-0">
                                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-sm shrink-0">
                                                {swap.requester.name.charAt(0)}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-slate-800 truncate">Posted by {swap.requester.name}</p>
                                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${swap.swapType === 'SWAP' ? 'bg-indigo-50 text-indigo-700' : 'bg-teal-50 text-teal-700'}`}>
                                                        {swap.swapType === 'SWAP' ? 'Swap' : 'Cover'}
                                                    </span>
                                                </div>
                                                <div className={`grid gap-2 mt-3 ${swap.swapType === 'SWAP' ? 'sm:grid-cols-2' : ''}`}>
                                                    <ShiftSummary title="Shift available" shift={swap.shift} owner={swap.requester.name} />
                                                    {swap.swapType === 'SWAP' && swap.targetShift && (
                                                        <ShiftSummary title="In exchange for" shift={swap.targetShift} owner={swap.targetShift.user?.name || 'Your matching shift'} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setConfirmAction({
                                                type: 'accept',
                                                id: swap.id,
                                                label: acceptLabelFor(swap)
                                            })}
                                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors shrink-0"
                                        >
                                            Accept
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* My Requests Tab */}
            {activeTab === 'mine' && (
                <div style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    {loading ? (
                        <div className="space-y-3">
                            {[1, 2].map(i => (
                                <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                                    <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
                                    <div className="h-3 bg-slate-100 rounded w-1/3" />
                                </div>
                            ))}
                        </div>
                    ) : mySwaps.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3"><svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2"/></svg></div>
                            <h3 className="text-lg font-semibold text-slate-700">No swap requests</h3>
                            <p className="text-slate-500 text-sm mt-1">Post a shift to see your requests here</p>
                            <button
                                onClick={() => setActiveTab('post')}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Post a Shift
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {mySwaps.map(swap => (
                                <div key={swap.id} className="bg-white rounded-xl border border-slate-200 p-5 transition-all hover:border-slate-300">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-slate-800">
                                                    {swap.swapType === 'SWAP' ? 'Shift swap' : 'Cover request'}
                                                </span>
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusStyles[swap.status]}`}>
                                                    {swap.status}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${swap.swapType === 'SWAP' ? 'bg-indigo-50 text-indigo-700' : 'bg-teal-50 text-teal-700'}`}>
                                                    {swap.swapType === 'SWAP' ? 'Swap' : 'Cover'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-slate-500 mt-1">
                                                {swap.targetUser
                                                    ? <>Accepted by <span className="font-medium text-slate-700">{swap.targetUser.name}</span></>
                                                    : <span className="text-blue-600 italic">Waiting for acceptor</span>
                                                }
                                            </p>
                                            <div className={`grid gap-2 mt-3 ${swap.swapType === 'SWAP' ? 'sm:grid-cols-2' : ''}`}>
                                                <ShiftSummary title="Your posted shift" shift={swap.shift} owner={swap.shift.user?.name || swap.requester.name} />
                                                {swap.swapType === 'SWAP' && swap.targetShift && (
                                                    <ShiftSummary title="Requested shift" shift={swap.targetShift} owner={swap.targetShift.user?.name || swap.targetUser?.name} />
                                                )}
                                            </div>
                                            {swap.adminNotes && (
                                                <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                                                    <span className="font-semibold">Admin notes:</span> {swap.adminNotes}
                                                </div>
                                            )}
                                        </div>
                                        {swap.status === 'PENDING' && swap.requesterId === currentUserId && (
                                            <button
                                                onClick={() => setConfirmAction({
                                                    type: 'cancel',
                                                    id: swap.id,
                                                    label: 'Are you sure you want to cancel this swap request?'
                                                })}
                                                className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors shrink-0"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                        {swap.status === 'PENDING' && swap.requesterId !== currentUserId && (
                                            <button
                                                onClick={() => setConfirmAction({
                                                    type: 'withdraw',
                                                    id: swap.id,
                                                    label: 'Are you sure you want to withdraw your acceptance of this swap?'
                                                })}
                                                className="px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors shrink-0"
                                            >
                                                Withdraw
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Post Swap Tab */}
            {activeTab === 'post' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    <form onSubmit={handlePostSwap}>
                        <div className="p-6">
                            {/* Step 1: Cover / Swap Toggle */}
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center">1</span>
                                    <span className="text-sm font-semibold text-slate-700">Choose Request Type</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => { setPostSwapType('COVER'); setSelectedTargetShiftId(''); clearTargetFilters(); }}
                                        className={`relative p-4 rounded-xl text-left transition-all border-2 group ${
                                            postSwapType === 'COVER'
                                                ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-emerald-50 shadow-sm'
                                                : 'border-slate-200 hover:border-slate-300 bg-white'
                                        }`}
                                    >
                                        {postSwapType === 'COVER' && (
                                            <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center">
                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                            </div>
                                        )}
                                        <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center mb-1"><svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg></div>
                                        <div className={`font-semibold text-sm ${postSwapType === 'COVER' ? 'text-teal-800' : 'text-slate-700'}`}>Cover</div>
                                        <p className={`text-xs mt-0.5 ${postSwapType === 'COVER' ? 'text-teal-600' : 'text-slate-400'}`}>Someone takes your shift</p>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPostSwapType('SWAP')}
                                        className={`relative p-4 rounded-xl text-left transition-all border-2 group ${
                                            postSwapType === 'SWAP'
                                                ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-blue-50 shadow-sm'
                                                : 'border-slate-200 hover:border-slate-300 bg-white'
                                        }`}
                                    >
                                        {postSwapType === 'SWAP' && (
                                            <div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center">
                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                            </div>
                                        )}
                                        <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center mb-1"><svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg></div>
                                        <div className={`font-semibold text-sm ${postSwapType === 'SWAP' ? 'text-indigo-800' : 'text-slate-700'}`}>Swap</div>
                                        <p className={`text-xs mt-0.5 ${postSwapType === 'SWAP' ? 'text-indigo-600' : 'text-slate-400'}`}>Exchange shifts with someone</p>
                                    </button>
                                </div>
                            </div>

                            {/* Step 2: Your Shift */}
                            <div className="mb-2">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center">2</span>
                                    <span className="text-sm font-semibold text-slate-700">
                                        {postSwapType === 'COVER' ? 'Select Shift to Give Away' : 'Select Your Shift'}
                                    </span>
                                </div>

                                {loadingShifts ? (
                                    <div className="space-y-2">
                                        {[1, 2].map(i => (
                                            <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : myShifts.length === 0 ? (
                                    <div className="bg-slate-50 rounded-xl p-6 text-center">
                                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center mb-1.5 mx-auto"><svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>
                                        <p className="text-slate-600 font-medium text-sm">No upcoming shifts</p>
                                        <p className="text-slate-400 text-xs mt-0.5">You need assigned shifts to create a request</p>
                                    </div>
                                ) : (
                                    <div className="max-h-[240px] overflow-y-auto space-y-1.5 pr-1 scrollbar-thin">
                                        {myShifts.map(shift => {
                                            const isSelected = selectedShiftId === shift.id;
                                            const typeStyle = shift.type && shiftTypeStyles[shift.type];
                                            return (
                                                <label
                                                    key={shift.id}
                                                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg cursor-pointer transition-all ${
                                                        isSelected
                                                            ? 'bg-blue-50 border border-blue-300 ring-1 ring-blue-200'
                                                            : 'border border-slate-150 hover:bg-slate-50'
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="shiftId"
                                                        value={shift.id}
                                                        checked={isSelected}
                                                        onChange={() => { setSelectedShiftId(shift.id); setSelectedTargetShiftId(''); clearTargetFilters(); }}
                                                        className="sr-only"
                                                    />
                                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                        isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                                                    }`}>
                                                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                        <p className={`font-medium text-sm truncate ${isSelected ? 'text-blue-800' : 'text-slate-700'}`}>
                                                            {formatShiftTime(shift.startTime, shift.endTime)}
                                                        </p>
                                                        {typeStyle && (
                                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${typeStyle.bg}`}>
                                                                {typeStyle.label}
                                                            </span>
                                                        )}
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Step 3: Target Shift (SWAP mode only) */}
                            {postSwapType === 'SWAP' && selectedShiftId && (
                                <>
                                    {/* Visual flow arrow */}
                                    <div className="flex justify-center py-2">
                                        <div className="flex flex-col items-center gap-0.5 text-indigo-400">
                                            <div className="w-px h-4 bg-indigo-200" />
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                                            <div className="w-px h-4 bg-indigo-200" />
                                        </div>
                                    </div>

                                    <div className="mb-2">
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">3</span>
                                            <span className="text-sm font-semibold text-slate-700">Select Shift You Want</span>
                                        </div>
                                        <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_auto]">
                                            <div className="grid gap-2 sm:grid-cols-2">
                                                <label className="block">
                                                    <span className="sr-only">Filter by staff name</span>
                                                    <input
                                                        value={targetStaffFilter}
                                                        onChange={e => {
                                                            setTargetStaffFilter(e.target.value);
                                                            setSelectedTargetShiftId('');
                                                        }}
                                                        placeholder="Search staff, e.g. Amit"
                                                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className="sr-only">Filter by date</span>
                                                    <input
                                                        type="date"
                                                        value={targetDateFilter}
                                                        onChange={e => {
                                                            setTargetDateFilter(e.target.value);
                                                            setSelectedTargetShiftId('');
                                                        }}
                                                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                                                    />
                                                </label>
                                            </div>
                                            {(targetStaffFilter || targetDateFilter) && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        clearTargetFilters();
                                                        setSelectedTargetShiftId('');
                                                    }}
                                                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50"
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                        <p className="mb-3 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-700 border border-indigo-100">
                                            Pick the other person's shift you want in exchange. Admin approval is still required before the roster changes.
                                        </p>
                                        {loadingTargetShifts ? (
                                            <div className="space-y-2">
                                                {[1, 2].map(i => (
                                                    <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                                                ))}
                                            </div>
                                        ) : targetShiftError ? (
                                            <div className="bg-red-50 rounded-xl p-4 text-center border border-red-200">
                                                <p className="text-red-700 text-sm font-medium">{targetShiftError}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const currentShift = selectedShiftId;
                                                        setSelectedShiftId('');
                                                        requestAnimationFrame(() => setSelectedShiftId(currentShift));
                                                    }}
                                                    className="mt-2 text-xs font-semibold text-red-600 underline"
                                                >
                                                    Try again
                                                </button>
                                            </div>
                                        ) : availableTargetShifts.length === 0 ? (
                                            <div className="bg-amber-50 rounded-xl p-4 text-center border border-amber-200">
                                                <p className="text-amber-700 text-sm font-medium">No matching shifts available</p>
                                                <p className="text-amber-500 text-xs mt-0.5">Only same-type shifts from other staff are shown</p>
                                            </div>
                                        ) : filteredTargetShifts.length === 0 ? (
                                            <div className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
                                                <p className="text-slate-700 text-sm font-medium">No shifts match these filters</p>
                                                <p className="text-slate-500 text-xs mt-0.5">Try a different staff name or date.</p>
                                            </div>
                                        ) : (
                                            <div className="max-h-[240px] overflow-y-auto space-y-1.5 pr-1 scrollbar-thin">
                                                {filteredTargetShifts.map(shift => {
                                                    const isSelected = selectedTargetShiftId === shift.id;
                                                    const typeStyle = shift.type && shiftTypeStyles[shift.type];
                                                    const ownerName = shift.userName;
                                                    return (
                                                        <label
                                                            key={shift.id}
                                                            className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg cursor-pointer transition-all ${
                                                                isSelected
                                                                    ? 'bg-indigo-50 border border-indigo-300 ring-1 ring-indigo-200'
                                                                    : 'border border-slate-150 hover:bg-slate-50'
                                                            }`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="targetShiftId"
                                                                value={shift.id}
                                                                checked={isSelected}
                                                                onChange={() => setSelectedTargetShiftId(shift.id)}
                                                                className="sr-only"
                                                            />
                                                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                                isSelected ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'
                                                            }`}>
                                                                {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <p className={`font-medium text-sm truncate ${isSelected ? 'text-indigo-800' : 'text-slate-700'}`}>
                                                                        {formatShiftTime(shift.startTime, shift.endTime)}
                                                                    </p>
                                                                    {typeStyle && (
                                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${typeStyle.bg}`}>
                                                                            {typeStyle.label}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {ownerName && (
                                                                    <p className="text-xs text-slate-400 mt-0.5">{ownerName}</p>
                                                                )}
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="p-5 bg-slate-50 border-t border-slate-100">
                            <button
                                type="submit"
                                disabled={posting || !selectedShiftId || (postSwapType === 'SWAP' && !selectedTargetShiftId)}
                                className={`w-full font-semibold py-3 px-6 rounded-xl transition-all shadow-sm hover:shadow-md disabled:shadow-none flex items-center justify-center gap-2 text-white disabled:bg-slate-300 disabled:cursor-not-allowed ${
                                    postSwapType === 'SWAP'
                                        ? 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700'
                                        : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700'
                                }`}
                            >
                                {posting ? (
                                    <>
                                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                        Posting...
                                    </>
                                ) : (
                                    postSwapType === 'COVER' ? 'Post Cover Request' : 'Post Swap Request'
                                )}
                            </button>
                            <p className="text-center text-xs text-slate-400 mt-2">
                                Your shift will be visible to qualified colleagues. Admin approval required.
                            </p>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default SwapMarketplace;
